import React, { useCallback, useEffect } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import SearchTile from 'components/SearchKit/SearchTiles/SearchTile'
import TextButton from 'LuxKit/Button/TextButton'
import Group from 'components/utils/Group'
import Clickable from 'components/Common/Clickable'
import Label from 'LuxKit/Label/Label'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import LinePlaneDepartureIcon from 'LuxKit/Icons/line/LinePlaneDepartureIcon'
import LineStarIcon from 'LuxKit/Icons/line/LineStarIcon'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import PriceRowLuxPlusPrice from 'LuxKit/PricePoints/LuxPlus/PriceRowLuxPlusPrice'
import PriceRowLuxPlusPriceLoadingSkeleton from 'LuxKit/PricePoints/LuxPlus/PriceRowLuxPlusPriceLoadingSkeleton'
import { useInView } from 'react-intersection-observer'
import * as Analytics from 'analytics/analytics'
import { promotionClickEvent, promotionViewEvent } from 'analytics/snowplow/events'
import { MALDIVES_CAMPAIGN_NAME, MALDIVES_BUNDLE_LABEL, MALDIVES_MARKETING_BANNER_IMAGE_ID } from 'constants/charterFlight'
import { mediaQueryUp } from 'components/utils/breakpoint'
import LineCalendarCheckIcon from 'LuxKit/Icons/line/LineCalendarCheckIcon'
import LineClockWithTickIcon from 'LuxKit/Icons/line/LineClockWithTickIcon'

const StyledClickable = styled(Clickable)`
  margin-bottom: -${rem(16)};;

  ${mediaQueryUp.tablet} {
    margin-bottom: 0;
  }
`

interface Props {
  onViewBundlesClick: () => void
  price: number
  nights: number
  isPriceLoading?: boolean
}

const MALDIVES_BANNER_IMAGE: App.Image = {
  id: MALDIVES_MARKETING_BANNER_IMAGE_ID,
  title: 'Maldives luxury resort with overwater bungalows',
}

const staticImages: Array<App.Image> = [MALDIVES_BANNER_IMAGE]

const staticOffer: App.HotelOffer = {
  id: 'maldives-banner-static',
  type: 'hotel',
  parentType: 'hotel',
  name: 'Maldives Hotel Bundles with Australia\'s First & Only Direct Flights to Malé',
  slug: 'maldives-direct-banner',
  productType: 'hotel',
  location: 'Maldives',
  locations: ['Maldives'],
  saleUnit: 'package',
} as App.HotelOffer

function MaldivesBanner({ onViewBundlesClick, price, nights, isPriceLoading = false }: Props) {
  // Intersection observer to track when banner comes into view
  const [inViewRef, inView] = useInView({
    triggerOnce: true,
    threshold: 0.5,
  })

  // Track promotion impression when banner comes into view
  useEffect(() => {
    if (inView) {
      Analytics.trackEvent(promotionViewEvent({
        promotionContext: {
          promotionName: MALDIVES_CAMPAIGN_NAME,
          promotionType: 'banner',
        },
      }))
    }
  }, [inView])

  // Track promotion click when customer clicks the banner
  const handleBannerClick = useCallback(() => {
    Analytics.trackEvent(promotionClickEvent({
      promotionContext: {
        promotionName: MALDIVES_CAMPAIGN_NAME,
        promotionType: 'banner',
      },
    }))
    onViewBundlesClick()
  }, [onViewBundlesClick])

  const features = (
    <Group direction="vertical" gap={4}>
      <BodyTextBlock variant="medium" startIcon={<LinePlaneDepartureIcon />}>
        Save up to 20 hours in transit with nonstop flights from Melbourne to Malé in less than 12 hours.
      </BodyTextBlock>
      <BodyTextBlock variant="medium" startIcon={<LineStarIcon />}>
        Luxury Escapes private charter aboard a Maldivian Airlines full-service Airbus A330-200.
      </BodyTextBlock>
      <BodyTextBlock variant="medium" startIcon={<LineClockWithTickIcon />}>
        Maximise your holiday time with early check-in & late checkout.
      </BodyTextBlock>
      <BodyTextBlock variant="medium" startIcon={<LineCalendarCheckIcon />}>
        Free cancellation within 7 days. Conditions apply.
      </BodyTextBlock>
    </Group>
  )

  const priceDisplay = isPriceLoading
    ? <PriceRowLuxPlusPriceLoadingSkeleton size="L" />
    : <PriceRowLuxPlusPrice size="L" memberPrice={price} saleUnit="person" logoPlacement="trailing" />

  const pricePoint = (
    <Group direction="vertical" gap={4}>
      <Group direction="horizontal" gap={4} verticalAlign="center">
        <LinePlaneDepartureIcon size="XS" colour="neutral-two" />
        <PriceRowPriceCaption>
          {nights} nights + flights from
        </PriceRowPriceCaption>
      </Group>
      {priceDisplay}
    </Group>
  )

  return (
    <StyledClickable onClick={handleBannerClick}>
      <SearchTile
        ref={inViewRef}
        offer={staticOffer}
        images={staticImages}
        title="Maldives Hotel Bundles with Australia's First & Only Direct Flights to Malé"
        features={features}
        pricePoint={pricePoint}
        action={<TextButton nonInteractable kind="primary">View bundle offers</TextButton>}
        productLabel={<Label kind="primary" variant="maldives-charter">{MALDIVES_BUNDLE_LABEL}</Label>}
        background="highlight-primary"
      />
    </StyledClickable>
  )
}

export default MaldivesBanner
